/**
 * Shared resources are to be used in both backend and frontend, were sst may not be available
 **/
import * as z from 'zod';
import Negotiator from 'negotiator';

export const categories = {
	basicNeeds: 0,
	humanRights: 1,
	education: 2,
	emergency: 3,
	feeding: 4,
	environment: 5,
	habitat: 6,
	health: 7,
} as const;

export const categoriesKeys = [
	'basicNeeds',
	'humanRights',
	'education',
	'emergency',
	'feeding',
	'environment',
	'habitat',
	'health',
] as const;

export type CategoryKey = (typeof categoriesKeys)[number];

export type Category = (typeof categories)[keyof typeof categories];

export const channelTypes = ['Corporate', 'Tpv', 'Widget', 'API', 'Custom'] as const;

export type ChannelType = (typeof channelTypes)[number];

export enum merchantOrigin {
	backoffice,
	onboarding,
	onboardingMagento,
	onboardingPrestashop,
	onboardingTpv,
}
export const WORLDCOO_MERCHANT_ID = '997d0641-65ab-4897-9719-e4977f733527';

export const languages = {
	Spanish: 'SPA',
	English: 'ENG',
	Catalan: 'CAT',
	Euskera: 'EUS',
	Gallego: 'GLG',
	German: 'GER',
	French: 'FRE',
	Korean: 'KOR',
	Chinese: 'CHI',
	Japanese: 'JPN',
	Portuguese: 'POR',
	Russian: 'RUS',
	Italian: 'ITA',
	Dutch: 'DUT',
	Greek: 'GRE',
} as const;

export type Language = (typeof languages)[keyof typeof languages];

export const locales = [
	'es',
	'en',
	'ca',
	'eu',
	'gl',
	'de',
	'fr',
	'ko',
	'zh',
	'ja',
	'pt',
	'ru',
	'it',
	'nl',
	'el',
] as const;

export const localesSchema = z.enum(locales);

export type Locale = z.infer<typeof localesSchema>;

export function detectLocale<T extends string>(acceptLanguage: string | null, availableLocales: readonly T[]): T {
	if (!acceptLanguage) return 'es' as T;
	const negotiator = new Negotiator({ headers: { 'accept-language': acceptLanguage } });
	return (negotiator.language(availableLocales as unknown as string[]) ?? 'es') as T;
}

export const localeToLanguage: Record<Locale, Language> = {
	es: languages.Spanish,
	en: languages.English,
	ca: languages.Catalan,
	eu: languages.Euskera,
	gl: languages.Gallego,
	de: languages.German,
	fr: languages.French,
	ko: languages.Korean,
	zh: languages.Chinese,
	ja: languages.Japanese,
	pt: languages.Portuguese,
	ru: languages.Russian,
	it: languages.Italian,
	nl: languages.Dutch,
	el: languages.Greek,
};

export function localeToLang(locale: string): Language | undefined {
	const normalizedLocale = locale.trim().toLowerCase();
	return localeToLanguage[normalizedLocale as keyof typeof localeToLanguage];
}

export const languageToLocale = {
	[languages.Spanish]: 'es',
	[languages.English]: 'en',
	[languages.Catalan]: 'ca',
	[languages.Euskera]: 'eu',
	[languages.Gallego]: 'gl',
	[languages.German]: 'de',
	[languages.French]: 'fr',
	[languages.Korean]: 'ko',
	[languages.Chinese]: 'zh',
	[languages.Japanese]: 'ja',
	[languages.Portuguese]: 'pt',
	[languages.Russian]: 'ru',
	[languages.Italian]: 'it',
	[languages.Dutch]: 'nl',
	[languages.Greek]: 'el',
} as const satisfies Partial<Record<Language, Locale>>;

const countryTranslations: Partial<Record<Language, Record<string, string>>> = {
	CAT: {
		AF: 'Afganistan',
		AX: 'Alandia',
		AL: 'Albània',
		DZ: 'Argelia',
		AS: 'Samoa',
		AD: 'Andorra',
		AO: 'Angola',
		AI: 'Anguilla',
		AQ: 'Antàrtida',
		AG: 'Antigua i Barbuda',
		AR: 'Argentina',
		AM: 'Armènia',
		AW: 'Aruba',
		AU: 'Austràlia',
		AT: 'Austria',
		AZ: 'Azerbaidjan',
		BS: 'Bahames',
		BH: 'Bahrein',
		BD: 'Bangla Desh',
		BB: 'Barbados',
		BY: 'Bielorússia',
		BE: 'Bèlgica',
		BZ: 'Belize',
		BJ: 'Benín',
		BM: 'Bermudes',
		BT: 'Bhutan',
		BO: 'Bolívia',
		BA: 'Bòsnia i Herzegovina',
		BW: 'Botswana',
		BV: 'Isla Bouvet',
		BR: 'Brasil',
		IO: "Territori Britànic de l'Oceà",
		BN: 'Brunei Darussalam',
		BG: 'Bulgària',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Cambodja',
		CM: 'Camerun',
		CA: 'Canadà',
		CV: 'Cap Verd',
		KY: 'Illes Caiman',
		CF: 'República Centreafricana',
		TD: 'Txad',
		CL: 'Xile',
		CN: 'Xina',
		CX: 'Illa de Nadal',
		CC: 'llles Cocos (Keeling)',
		CO: 'Colòmbia',
		KM: 'Comores',
		CG: 'Congo',
		CD: 'Congo (Rep. Dem.)',
		CK: 'Illess Cook',
		CR: 'Costa Rica',
		CI: "Costa d'Ivori",
		HR: 'Croàcia',
		CU: 'Cuba',
		CY: 'Xipre',
		CZ: 'República Txeca',
		DK: 'Dinamarca',
		DJ: 'Djibouti',
		DM: 'Dominica',
		DO: 'República Dominicana',
		EC: 'Equador',
		EG: 'Egipte',
		SV: 'El Salvador',
		GQ: 'Guinea Equatorial',
		ER: 'Eritrea',
		EE: 'Estònia',
		ET: 'Etiòpia',
		FK: 'Illes Malvines',
		FO: 'Illes Fèroe',
		FJ: 'Fiji',
		FI: 'Finlàndia',
		FR: 'França',
		GF: 'Guaiana Francesa',
		PF: 'Polinèsia Francesa',
		TF: 'Terres Australs i Antàrtiques Franceses',
		GA: 'Gabon',
		GM: 'Gàmbia',
		GE: 'Geòrgia',
		DE: 'Alemanya',
		GH: 'Ghana',
		GI: 'Gibraltar',
		GR: 'Grècia',
		GL: 'Groenlàndia',
		GD: 'Granada',
		GP: 'Guadalupe',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernsey',
		GN: 'Guinea',
		GW: 'Guinea-Bissáu',
		GY: 'Guaiana',
		HT: 'Haití',
		HM: 'Illes Heard i McDonald',
		VA: 'Ciutat del Vaticà',
		HN: 'Hondures',
		HK: 'Hong Kong',
		HU: 'Hongria',
		IS: 'Islàndia',
		IN: 'Índia',
		ID: 'Indonèsia',
		IR: 'Iran',
		IQ: 'Iraq',
		IE: 'Irlanda',
		IM: 'Illa de Man',
		IL: 'Israel',
		IT: 'Itàlia',
		JM: 'Jamaica',
		JP: 'Japó',
		JE: 'Jersey',
		JO: 'Jordània',
		KZ: 'Kazakhstan',
		KE: 'Kenya',
		KI: 'Kiribati',
		KP: 'Corea del Nord',
		KR: 'Corea del Sud',
		KW: 'Kuwait',
		KG: 'Kirguizistan',
		LA: 'Laos',
		LV: 'Letònia',
		LB: 'Líban',
		LS: 'Lesotho',
		LR: 'Libèria',
		LY: 'Líbia',
		LI: 'Liechtenstein',
		LT: 'Lituània',
		LU: 'Luxemburg',
		MO: 'Macau',
		MK: 'Macedònia',
		MG: 'Madagascar',
		MW: 'Malawi',
		MY: 'Malàisia',
		MV: 'Maldives',
		ML: 'Mali',
		MT: 'Malta',
		MH: 'Illes Marshall',
		MQ: 'Martinica',
		MR: 'Mauritània',
		MU: 'Maurici',
		YT: 'Mayotte',
		MX: 'Mèxic',
		FM: 'Micronèsia',
		MD: 'Moldàvia',
		MC: 'Mònaco',
		MN: 'Mongòlia',
		MS: 'Montserrat',
		MA: 'Marroc',
		MZ: 'Moçambic',
		MM: 'Myanmar',
		NA: 'Namíbia',
		NR: 'Nauru',
		NP: 'Nepal',
		NL: 'Països Baixos',
		AN: 'Antilles Holandeses',
		NC: 'Nova Caledònia',
		NZ: 'Nova Zelanda',
		NI: 'Nicaragua',
		NE: 'Níger',
		NG: 'Nigèria',
		NU: 'Niue',
		NF: 'Illa de Norfolk',
		MP: 'Illes Mariannes',
		NO: 'Noruega',
		OM: 'Oman',
		PK: 'Pakistan',
		PW: 'Palau',
		PS: 'Palestina',
		PA: 'Panamà',
		PG: 'Papua Nova Guinea',
		PY: 'Paraguai',
		PE: 'Perú',
		PH: 'Filipines',
		PN: 'Illes Pitcairn',
		PL: 'Polònia',
		PT: 'Portugal',
		PR: 'Puerto Rico',
		QA: 'Qatar',
		RE: 'Reunió',
		RO: 'Romania',
		RU: 'Rússia',
		RW: 'Rwanda',
		SH: 'Saint Helena',
		KN: 'Saint Christopher i Nevis ',
		LC: 'Saint Lucia',
		PM: 'Saint-Pierre-et-Miquelon',
		VC: 'Saint Vincent i les Grenadines',
		WS: 'Samoa',
		SM: 'San Marino',
		ST: 'São Tomé i Príncipe',
		SA: 'Aràbia Saudita',
		SN: 'Senegal',
		CS: 'Sèrbia y Montenegro',
		SC: 'Seychelles',
		SL: 'Sierra Leone',
		SG: 'Singapur',
		SK: 'Eslovàquia',
		SI: 'Eslovènia',
		SB: 'Islles Solomó',
		SO: 'Somàlia',
		ZA: 'Sud-àfrica',
		GS: 'Illes Geòrgia del Sud i Sandwich del Sud',
		ES: 'Espanya',
		LK: 'Sri Lanka',
		SD: 'Sudan',
		SS: 'Sudan del sud',
		SR: 'Surinam',
		SJ: 'Illes Svalbard y Jan Mayen',
		SZ: 'Swazilàndia',
		SE: 'Suècia',
		CH: 'Suïssa',
		SY: 'Síria',
		TW: 'Taiwan',
		TJ: 'Tadjikistan',
		TZ: 'Tanzània',
		TH: 'Tailàndia',
		TL: 'Timor Oriental',
		TG: 'Togo',
		TK: 'Tokelau',
		TO: 'Tonga',
		TT: 'Trinitat i Tobago ',
		TN: 'Tunísia',
		TR: 'Turquia',
		TM: 'Turkmenistan',
		TC: 'Illes Turks y Caicos',
		TV: 'Tuvalu',
		UG: 'Uganda',
		UA: 'Ucraïna',
		AE: 'Emirats Àrabs Units',
		GB: 'Regne Unit',
		US: "Estats Units d'Amèrica",
		UM: 'Illes Ultramarines Menors dels Estats Units',
		UY: 'Uruguai',
		UZ: 'Uzbekistan',
		VU: 'Vanuatu',
		VE: 'Veneçuela',
		VN: 'Vietnam',
		VG: 'Illes Verges del Regne Unit',
		VI: 'Illes Verges dels Estats Units',
		WF: 'Wallis y Futuna',
		EH: 'Sahara Occidental',
		YE: 'Iemen',
		ZM: 'Zàmbia',
		ZW: 'Zimbabwe',
	},
	ENG: {
		AF: 'Afghanistan',
		AX: 'Aland Islands',
		AL: 'Albania',
		DZ: 'Algeria',
		AS: 'American Samoa',
		AD: 'Andorra',
		AO: 'Angola',
		AI: 'Anguilla',
		AQ: 'Antarctica',
		AG: 'Antigua and Barbuda',
		AR: 'Argentina',
		AM: 'Armenia',
		AW: 'Aruba',
		AU: 'Australia',
		AT: 'Austria',
		AZ: 'Azerbaijan',
		BS: 'Bahamas',
		BH: 'Bahrain',
		BD: 'Bangladesh',
		BB: 'Barbados',
		BY: 'Belarus',
		BE: 'Belgium',
		BZ: 'Belize',
		BJ: 'Benin',
		BM: 'Bermuda',
		BT: 'Bhutan',
		BO: 'Bolivia',
		BA: 'Bosnia and Herzegovina',
		BW: 'Botswana',
		BV: 'Bouvet Island',
		BR: 'Brazil',
		IO: 'British Indian Ocean Territory',
		BN: 'Brunei Darussalam',
		BG: 'Bulgaria',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Cambodia',
		CM: 'Cameroon',
		CA: 'Canada',
		CV: 'Cape Verde',
		KY: 'Cayman Islands',
		CF: 'Central African Republic',
		TD: 'Chad',
		CL: 'Chile',
		CN: 'China',
		CX: 'Christmas Island',
		CC: 'Cocos (Keeling) Islands',
		CO: 'Colombia',
		KM: 'Comoros',
		CG: 'Congo',
		CD: 'Congo, The Democratic Republic of the',
		CK: 'Cook Islands',
		CR: 'Costa Rica',
		CI: "Cote D'Ivoire",
		HR: 'Croatia',
		CU: 'Cuba',
		CY: 'Cyprus',
		CZ: 'Czech Republic',
		DK: 'Denmark',
		DJ: 'Djibouti',
		DM: 'Dominica',
		DO: 'Dominican Republic',
		EC: 'Ecuador',
		EG: 'Egypt',
		SV: 'El Salvador',
		GQ: 'Equatorial Guinea',
		ER: 'Eritrea',
		EE: 'Estonia',
		ET: 'Ethiopia',
		FK: 'Falkland Islands (Malvinas)',
		FO: 'Faroe Islands',
		FJ: 'Fiji',
		FI: 'Finland',
		FR: 'France',
		GF: 'French Guiana',
		PF: 'French Polynesia',
		TF: 'French Southern Territories',
		GA: 'Gabon',
		GM: 'Gambia',
		GE: 'Georgia',
		DE: 'Germany',
		GH: 'Ghana',
		GI: 'Gibraltar',
		GR: 'Greece',
		GL: 'Greenland',
		GD: 'Grenada',
		GP: 'Guadeloupe',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernsey',
		GN: 'Guinea',
		GW: 'Guinea-Bissau',
		GY: 'Guyana',
		HT: 'Haiti',
		HM: 'Heard Island and Mcdonald Islands',
		VA: 'Holy See (Vatican City State)',
		HN: 'Honduras',
		HK: 'Hong Kong',
		HU: 'Hungary',
		IS: 'Iceland',
		IN: 'India',
		ID: 'Indonesia',
		IR: 'Iran, Islamic Republic Of',
		IQ: 'Iraq',
		IE: 'Ireland',
		IM: 'Isle of Man',
		IL: 'Israel',
		IT: 'Italy',
		JM: 'Jamaica',
		JP: 'Japan',
		JE: 'Jersey',
		JO: 'Jordan',
		KZ: 'Kazakhstan',
		KE: 'Kenya',
		KI: 'Kiribati',
		KP: "Korea, Democratic People's Republic of",
		KR: 'Korea, Republic of',
		KW: 'Kuwait',
		KG: 'Kyrgyzstan',
		LA: "Lao People'S Democratic Republic",
		LV: 'Latvia',
		LB: 'Lebanon',
		LS: 'Lesotho',
		LR: 'Liberia',
		LY: 'Libyan Arab Jamahiriya',
		LI: 'Liechtenstein',
		LT: 'Lithuania',
		LU: 'Luxembourg',
		MO: 'Macao',
		MK: 'Macedonia, The Former Yugoslav Republic of',
		MG: 'Madagascar',
		MW: 'Malawi',
		MY: 'Malaysia',
		MV: 'Maldives',
		ML: 'Mali',
		MT: 'Malta',
		MH: 'Marshall Islands',
		MQ: 'Martinique',
		MR: 'Mauritania',
		MU: 'Mauritius',
		YT: 'Mayotte',
		MX: 'Mexico',
		FM: 'Micronesia, Federated States of',
		MD: 'Moldova, Republic of',
		MC: 'Monaco',
		MN: 'Mongolia',
		MS: 'Montserrat',
		MA: 'Morocco',
		MZ: 'Mozambique',
		MM: 'Myanmar',
		NA: 'Namibia',
		NR: 'Nauru',
		NP: 'Nepal',
		NL: 'Netherlands',
		AN: 'Netherlands Antilles',
		NC: 'New Caledonia',
		NZ: 'New Zealand',
		NI: 'Nicaragua',
		NE: 'Niger',
		NG: 'Nigeria',
		NU: 'Niue',
		NF: 'Norfolk Island',
		MP: 'Northern Mariana Islands',
		NO: 'Norway',
		OM: 'Oman',
		PK: 'Pakistan',
		PW: 'Palau',
		PS: 'Palestinian Territory, Occupied',
		PA: 'Panama',
		PG: 'Papua New Guinea',
		PY: 'Paraguay',
		PE: 'Peru',
		PH: 'Philippines',
		PN: 'Pitcairn',
		PL: 'Poland',
		PT: 'Portugal',
		PR: 'Puerto Rico',
		QA: 'Qatar',
		RE: 'Reunion',
		RO: 'Romania',
		RU: 'Russian Federation',
		RW: 'RWANDA',
		SH: 'Saint Helena',
		KN: 'Saint Kitts and Nevis',
		LC: 'Saint Lucia',
		PM: 'Saint Pierre and Miquelon',
		VC: 'Saint Vincent and the Grenadines',
		WS: 'Samoa',
		SM: 'San Marino',
		ST: 'Sao Tome and Principe',
		SA: 'Saudi Arabia',
		SN: 'Senegal',
		CS: 'Serbia and Montenegro',
		SC: 'Seychelles',
		SL: 'Sierra Leone',
		SG: 'Singapore',
		SK: 'Slovakia',
		SI: 'Slovenia',
		SB: 'Solomon Islands',
		SO: 'Somalia',
		ZA: 'South Africa',
		GS: 'South Georgia and the South Sandwich Islands',
		ES: 'Spain',
		LK: 'Sri Lanka',
		SD: 'Sudan',
		SS: 'South Sudan',
		SR: 'Suriname',
		SJ: 'Svalbard and Jan Mayen',
		SZ: 'Swaziland',
		SE: 'Sweden',
		CH: 'Switzerland',
		SY: 'Syrian Arab Republic',
		TW: 'Taiwan, Province of China',
		TJ: 'Tajikistan',
		TZ: 'Tanzania, United Republic of',
		TH: 'Thailand',
		TL: 'Timor-Leste',
		TG: 'Togo',
		TK: 'Tokelau',
		TO: 'Tonga',
		TT: 'Trinidad and Tobago',
		TN: 'Tunisia',
		TR: 'Turkey',
		TM: 'Turkmenistan',
		TC: 'Turks and Caicos Islands',
		TV: 'Tuvalu',
		UG: 'Uganda',
		UA: 'Ukraine',
		AE: 'United Arab Emirates',
		GB: 'United Kingdom',
		US: 'United States',
		UM: 'United States Minor Outlying Islands',
		UY: 'Uruguay',
		UZ: 'Uzbekistan',
		VU: 'Vanuatu',
		VE: 'Venezuela',
		VN: 'Vietnam',
		VG: 'Virgin Islands, British',
		VI: 'Virgin Islands, U.S.',
		WF: 'Wallis and Futuna',
		EH: 'Western Sahara',
		YE: 'Yemen',
		ZM: 'Zambia',
		ZW: 'Zimbabwe',
	},
	SPA: {
		AF: 'Afganistán',
		AX: 'Alandia',
		AL: 'Albania',
		DZ: 'Algeria',
		AS: 'Samoa Americana',
		AD: 'Andorra',
		AO: 'Angola',
		AI: 'Anguilla',
		AQ: 'Antártida',
		AG: 'Antigua y Barbuda',
		AR: 'Argentina',
		AM: 'Armenia',
		AW: 'Aruba',
		AU: 'Australia',
		AT: 'Austria',
		AZ: 'Azerbaiyán',
		BS: 'Bahamas',
		BH: 'Bahrein',
		BD: 'Bangladesh',
		BB: 'Barbados',
		BY: 'Bielorrusia',
		BE: 'Bélgica',
		BZ: 'Belize',
		BJ: 'Benín',
		BM: 'Bermudas',
		BT: 'Bhután',
		BO: 'Bolivia',
		BA: 'Bosnia y Herzegovina',
		BW: 'Botswana',
		BV: 'Isla Bouvet',
		BR: 'Brasil',
		IO: 'Territorio Británico del Océano',
		BN: 'Brunei Darussalam',
		BG: 'Bulgaria',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Camboya',
		CM: 'Camerún',
		CA: 'Canadá',
		CV: 'Cabo Verde',
		KY: 'Islas Caimán',
		CF: 'República Centroafricana',
		TD: 'Chad',
		CL: 'Chile',
		CN: 'China',
		CX: 'Isla de navidad',
		CC: 'Islas Cocos (Keeling)',
		CO: 'Colombia',
		KM: 'Comoras',
		CG: 'Congo',
		CD: 'Congo (Rep. Dem.)',
		CK: 'Islas Cook',
		CR: 'Costa Rica',
		CI: 'Costa de Marfil',
		HR: 'Croacia',
		CU: 'Cuba',
		CY: 'Chipre',
		CZ: 'República Checa',
		DK: 'Dinamarca',
		DJ: 'Djibouti',
		DM: 'Dominica',
		DO: 'República Dominicana',
		EC: 'Ecuador',
		EG: 'Egipto',
		SV: 'El Salvador',
		GQ: 'Guinea Equatorial',
		ER: 'Eritrea',
		EE: 'Estonia',
		ET: 'Etiopía',
		FK: 'Islas Malvinas',
		FO: 'Islas Feroe',
		FJ: 'Fiji',
		FI: 'Finlandia',
		FR: 'Francia',
		GF: 'Guyana Francesa',
		PF: 'Polinesia Francesa',
		TF: 'Tierras Australes y Antárticas Francesas',
		GA: 'Gabón',
		GM: 'Gambia',
		GE: 'Georgia',
		DE: 'Alemania',
		GH: 'Ghana',
		GI: 'Gibraltar',
		GR: 'Grecia',
		GL: 'Groenlandia',
		GD: 'Granada',
		GP: 'Guadalupe',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernsey',
		GN: 'Guinea',
		GW: 'Guinea-Bissáu',
		GY: 'Guyana',
		HT: 'Haití',
		HM: 'Islas Heard y McDonald',
		VA: 'Ciudad del Vaticano',
		HN: 'Honduras',
		HK: 'Hong Kong',
		HU: 'Hungría',
		IS: 'Islandia',
		IN: 'India',
		ID: 'Indonesia',
		IR: 'Iran',
		IQ: 'Iraq',
		IE: 'Irlanda',
		IM: 'Isla de Man',
		IL: 'Israel',
		IT: 'Italia',
		JM: 'Jamaica',
		JP: 'Japón',
		JE: 'Jersey',
		JO: 'Jordania',
		KZ: 'Kazajistán',
		KE: 'Kenia',
		KI: 'Kiribati',
		KP: 'Corea del Norte',
		KR: 'Corea del Sur',
		KW: 'Kuwait',
		KG: 'Kirguizistán',
		LA: 'Laos',
		LV: 'Letonia',
		LB: 'Líbano',
		LS: 'Lesotho',
		LR: 'Liberia',
		LY: 'Libia',
		LI: 'Liechtenstein',
		LT: 'Lituania',
		LU: 'Luxemburgo',
		MO: 'Macao',
		MK: 'Macedonia',
		MG: 'Madagascar',
		MW: 'Malawi',
		MY: 'Malasia',
		MV: 'Maldivas',
		ML: 'Mali',
		MT: 'Malta',
		MH: 'Islas Marshall',
		MQ: 'Martinica',
		MR: 'Mauritania',
		MU: 'Mauricio',
		YT: 'Mayotte',
		MX: 'México',
		FM: 'Micronesia',
		MD: 'Moldavia',
		MC: 'Mónaco',
		MN: 'Mongolia',
		MS: 'Montserrat',
		MA: 'Marruecos',
		MZ: 'Mozambique',
		MM: 'Myanmar',
		NA: 'Namibia',
		NR: 'Nauru',
		NP: 'Nepal',
		NL: 'Países Bajos',
		AN: 'Antillas Holandesas',
		NC: 'Nueva Caledonia',
		NZ: 'Nueva Zelanda',
		NI: 'Nicaragua',
		NE: 'Níger',
		NG: 'Nigeria',
		NU: 'Niue',
		NF: 'Isla de Norfolk',
		MP: 'Islas Marianas del Norte',
		NO: 'Noruega',
		OM: 'Omán',
		PK: 'Pakistán',
		PW: 'Palau',
		PS: 'Palestina',
		PA: 'Panamá',
		PG: 'Papúa Nueva Guinea',
		PY: 'Paraguay',
		PE: 'Perú',
		PH: 'Filipinas',
		PN: 'Islas Pitcairn',
		PL: 'Polonia',
		PT: 'Portugal',
		PR: 'Puerto Rico',
		QA: 'Qatar',
		RE: 'Reunión',
		RO: 'Rumanía',
		RU: 'Rusia',
		RW: 'Ruanda',
		SH: 'Santa Helena',
		KN: 'San Cristóbal y Nieves',
		LC: 'Santa Lucia',
		PM: 'San Pedro y Miquelón',
		VC: 'San Vicente y Granadinas',
		WS: 'Samoa',
		SM: 'San Marino',
		ST: 'Santo Tomé y Principe',
		SA: 'Arabia Saudí',
		SN: 'Senegal',
		CS: 'Serbia y Montenegro',
		SC: 'Seychelles',
		SL: 'Sierra Leona',
		SG: 'Singapur',
		SK: 'República Eslovaca',
		SI: 'Eslovenia',
		SB: 'Islas Solomón',
		SO: 'Somalia',
		ZA: 'República de Sudáfrica',
		GS: 'Islas Georgias del Sur y Sandwich del Sur',
		ES: 'España',
		LK: 'Sri Lanka',
		SD: 'Sudán',
		SS: 'Sudán del Sur',
		SR: 'Surinam',
		SJ: 'Islas Svalbard y Jan Mayen',
		SZ: 'Suazilandia',
		SE: 'Suecia',
		CH: 'Suiza',
		SY: 'Siria',
		TW: 'Taiwán',
		TJ: 'Tayikistán',
		TZ: 'Tanzania',
		TH: 'Tailandia',
		TL: 'Timor Oriental',
		TG: 'Togo',
		TK: 'Tokelau',
		TO: 'Tonga',
		TT: 'Trinidad y Tobago',
		TN: 'Túnez',
		TR: 'Turquía',
		TM: 'Turkmenistán',
		TC: 'Islas Turks y Caicos',
		TV: 'Tuvalu',
		UG: 'Uganda',
		UA: 'Ucrania',
		AE: 'Emiratos Árabes Unidos',
		GB: 'Reino Unido',
		US: 'Estados Unidos de América',
		UM: 'Islas Ultramarinas Menores de Estados Unidos',
		UY: 'Uruguay',
		UZ: 'Uzbekistán',
		VU: 'Vanuatu',
		VE: 'Venezuela',
		VN: 'Vietnam',
		VG: 'Islas Vírgenes del Reino Unido',
		VI: 'Islas Vírgenes de los Estados Unidos',
		WF: 'Wallis y Futuna',
		EH: 'Sahara Occidental',
		YE: 'Yemen',
		ZM: 'Zambia',
		ZW: 'Zimbabue',
	},
	POR: {
		AF: 'Afeganistão',
		AX: 'Alandia',
		AL: 'Albânia',
		DZ: 'Argélia',
		AS: 'Samoa Americana',
		AD: 'Andorra',
		AO: 'Angola',
		AI: 'Anguilla',
		AQ: 'Antártica',
		AG: 'Antigua e Barbuda',
		AR: 'Argentina',
		AM: 'Armenia',
		AW: 'Aruba',
		AU: 'Austrália',
		AT: 'Áustria',
		AZ: 'Azerbaijão',
		BS: 'Bahamas',
		BH: 'Bahrein',
		BD: 'Bangladesh',
		BB: 'Barbados',
		BY: 'Bielorrússia',
		BE: 'Bélgica',
		BZ: 'Belize',
		BJ: 'Benin',
		BM: 'Bermuda',
		BT: 'Bhutan',
		BO: 'Bolívia',
		BA: 'Bósnia e Herzegovina',
		BW: 'Botswana',
		BV: 'Ilha Bouvet',
		BR: 'Brasil',
		IO: 'British Ocean Territory',
		BN: 'Brunei Darussalam',
		BG: 'Bulgária',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Camboja',
		CM: 'Camarões',
		CA: 'Canadá',
		CV: 'Cabo Verde',
		KY: 'Ilhas Cayman',
		CF: 'República Centro-Africana',
		TD: 'Chad',
		CL: 'Chile',
		CN: 'China',
		CX: 'Christmas Island',
		CC: 'Ilhas Cocos (Keeling)',
		CO: 'Colômbia',
		KM: 'Comores',
		CG: 'Congo',
		CD: 'Congo (Dem. Rep.)',
		CK: 'Ilhas Cook',
		CR: 'Costa Rica',
		CI: "Côte d'Ivoire",
		HR: 'Croácia',
		CU: 'Cuba',
		CY: 'Chipre',
		CZ: 'República Checa',
		DK: 'Dinamarca',
		DJ: 'Djibouti',
		DM: 'Dominica',
		DO: 'República Dominicana',
		EC: 'Equador',
		EG: 'Egito',
		SV: 'El Salvador',
		GQ: 'Guiné Equatorial',
		ER: 'Eritréia',
		EE: 'Estónia',
		ET: 'Etiópia',
		FK: 'Islas Malvinas',
		FO: 'Faroe Islands',
		FJ: 'Fiji',
		FI: 'Finlândia',
		FR: 'França',
		GF: 'Guiana Francesa',
		PF: 'Polinésia Francesa',
		TF: 'Terras francesas do sul e da Antártida',
		GA: 'Gabão',
		GM: 'Gâmbia',
		GE: 'Georgia',
		DE: 'Alemanha',
		GH: 'Gana',
		GI: 'Gibraltar',
		GR: 'Grécia',
		GL: 'Greenland',
		GD: 'Granada',
		GP: 'Guadalupe',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernsey',
		GN: 'Guiné',
		GW: 'Guiné-Bissáu',
		GY: 'Guiana',
		HT: 'Haiti',
		HM: 'Heard and McDonald Islands',
		VA: 'Cidade do Vaticano',
		HN: 'Honduras',
		HK: 'Hong Kong',
		HU: 'Hungria',
		IS: 'Islândia',
		IN: 'Índia',
		ID: 'Indonésia',
		IR: 'Irã',
		QI: 'Iraque',
		IE: 'Irlanda',
		IM: 'Isle of Man',
		IL: 'Israel',
		IT: 'Itália',
		JM: 'Jamaica',
		JP: 'Japão',
		JE: 'Jersey',
		JO: 'Jordan',
		KZ: 'Cazaquistão',
		KE: 'Quênia',
		KI: 'Kiribati',
		KP: 'Coréia do Norte',
		KR: 'Coréia do Sul',
		KW: 'Kuwait',
		KG: 'Quirguistão',
		LA: 'Laos',
		LV: 'Letónia',
		LB: 'Líbano',
		LS: 'Lesotho',
		LR: 'Liberia',
		LY: 'Líbia',
		LI: 'Liechtenstein',
		LT: 'Lituânia',
		LU: 'Luxemburgo',
		MO: 'Macao',
		MK: 'Macedonia',
		MG: 'Madagascar',
		MW: 'Malawi',
		MY: 'Malásia',
		MV: 'Maldivas',
		ML: 'Mali',
		MT: 'Malta',
		MH: 'Marshall Islands',
		MQ: 'Martinica',
		MR: 'Mauritânia',
		MU: 'Mauricio',
		YT: 'Mayotte',
		MX: 'México',
		FM: 'Micronésia',
		MD: 'Moldova',
		MC: 'Monaco',
		MN: 'Mongólia',
		MS: 'Montserrat',
		MA: 'Marrocos',
		MZ: 'Moçambique',
		MM: 'Myanmar',
		NA: 'Namíbia',
		NR: 'Nauru',
		NP: 'Nepal',
		NL: 'Países Baixos',
		AN: 'Antilhas Holandesas',
		NC: 'Nova Caledônia',
		NZ: 'Nova Zelândia',
		NI: 'Nicarágua',
		NE: 'Níger',
		NG: 'Nigéria',
		NU: 'Niue',
		NF: 'Ilha de Norfolk',
		MP: 'Northern Mariana Islands',
		NO: 'Noruega',
		OM: 'Omã',
		PK: 'Paquistão',
		PW: 'Palau',
		PS: 'Palestina',
		PA: 'Panamá',
		PG: 'Papua Nova Guiné',
		PY: 'Paraguai',
		PE: 'Peru',
		PH: 'Filipinas',
		PN: 'Pitcairn Islands',
		PL: 'Polônia',
		PT: 'Portugal',
		PR: 'Puerto Rico',
		QA: 'Qatar',
		RE: 'Meeting',
		RO: 'Roménia',
		RU: 'Rússia',
		RW: 'Ruanda',
		SH: 'Santa Helena',
		KN: 'São Cristóvão e Nevis',
		LC: 'Santa Lucia',
		PM: 'São Pedro e Miquelon',
		VC: 'São Vicente e Granadinas',
		WS: 'Samoa',
		SM: 'San Marino',
		ST: 'Santo Tomé e Principe',
		SA: 'Arábia Saudita',
		SN: 'Senegal',
		CS: 'Sérvia e Montenegro',
		SC: 'Seychelles',
		SL: 'Serra Leoa',
		SG: 'Singapura',
		SK: 'República Eslovaca',
		SI: 'Eslovênia',
		SB: 'Ilhas Salomão',
		SO: 'Somália',
		ZA: 'República da África do Sul',
		GS: 'Geórgia do Sul e Ilhas Sandwich do Sul',
		ES: 'Espanha',
		LK: 'Sri Lanka',
		SD: 'Sudão',
		SS: 'Sudão do Sul',
		SR: 'Suriname',
		SJ: 'Svalbard Islands e Jan Mayen',
		SZ: 'Swazilândia',
		SE: 'Suécia',
		CH: 'Suíça',
		SY: 'Síria',
		TW: 'Taiwan',
		TJ: 'Tajiquistão',
		TZ: 'Tanzânia',
		TH: 'Tailândia',
		TL: 'Timor Leste',
		TG: 'Togo',
		TK: 'Tokelau',
		TO: 'Tonga',
		TT: 'Trinidad e Tobago',
		TN: 'Tunísia',
		TR: 'Turquía',
		TM: 'Turkmenistan',
		TC: 'Ilhas Turcas e Caicos',
		TV: 'Tuvalu',
		UG: 'Uganda',
		UA: 'Ucrânia',
		AE: 'Emirados Árabes Unidos',
		GB: 'Reino Unido',
		US: 'Estados Unidos da América',
		UM: 'Ilhas Menores Distantes dos Estados Unidos',
		UY: 'Uruguai',
		UZ: 'Uzbequistão',
		VU: 'Vanuatu',
		VE: 'Venezuela',
		VN: 'Vietnã',
		VG: 'Ilhas Virgens do Reino Unido',
		VI: 'Ilhas Virgens dos Estados Unidos',
		WF: 'Wallis e Futuna',
		EH: 'Sara Ocidental',
		YE: 'Iêmen',
		ZM: 'Zâmbia',
		ZW: 'Zimbabwe',
	},
	FRE: {
		AF: 'Afghanistan',
		AX: 'Alandia',
		AL: 'Albanie',
		DZ: 'Algérie',
		AS: 'Samoa américaines',
		AD: 'Andorre',
		AO: 'Angola',
		AI: 'Anguilla',
		AQ: 'Antarctique',
		AG: 'Antigua et Barbuda',
		AR: 'Argentine',
		AM: 'Arménie',
		AW: 'Aruba',
		AU: 'Australie',
		AT: 'Autriche',
		AZ: 'Azerbaïdjan',
		BS: 'Bahamas',
		BH: 'Bahreïn',
		BD: 'Bangladesh',
		BB: 'Barbade',
		BY: 'Biélorussie',
		BE: 'Belgique',
		BZ: 'Belize',
		BJ: 'Bénin',
		BM: 'Bermudes',
		BT: 'Bhoutan',
		BO: 'Bolivie',
		BA: 'Bosnie-Herzégovine',
		BW: 'Botswana',
		BV: 'Île Bouvet',
		BR: 'Brésil',
		IO: "Territoire de l'océan britannique",
		BN: 'Brunéi Darussalam',
		BG: 'Bulgarie',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Cambodge',
		CM: 'Cameroun',
		CA: 'Canada',
		CV: 'Cap-Vert',
		KY: 'Îles Caïmans',
		CF: 'République centrafricaine',
		TD: 'Tchad',
		CL: 'Chili',
		CN: 'Chine',
		CX: 'Île de Noël',
		CC: 'Îles Cocos (Keeling)',
		CO: 'Colombie',
		KM: 'Comores',
		CG: 'Congo',
		CD: 'Congo (Rép. Dém.)',
		CK: 'Îles Cook',
		CR: 'Costa Rica',
		CI: "Côte d'Ivoire",
		HR: 'Croatie',
		CU: 'Cuba',
		CY: 'Chypre',
		CZ: 'République tchèque',
		DK: 'Danemark',
		DJ: 'Djibouti',
		DM: 'Dominique',
		DO: 'République dominicaine',
		EC: 'Équateur',
		EG: 'Egypte',
		SV: 'El Salvador',
		GQ: 'Guinée équatoriale',
		ER: 'Érythrée',
		EE: 'Estonie',
		ET: 'Ethiopie',
		FK: 'Islas Malvinas',
		FO: 'Îles Féroé',
		FJ: 'Fidji',
		FI: 'Finlande',
		FR: 'France',
		GF: 'Guyane française',
		PF: 'Polynésie française',
		TF: 'Terres australes et antarctiques françaises',
		GA: 'Gabon',
		GM: 'Gambie',
		GE: 'Géorgie',
		DE: 'Allemagne',
		GH: 'Ghana',
		GI: 'Gibraltar',
		GR: 'Grèce',
		GL: 'Groenland',
		GD: 'Grenade',
		GP: 'Guadalupe',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernesey',
		GN: 'Guinée',
		GW: 'Guinée-Bissáu',
		GY: 'Guyana',
		HT: 'Haïti',
		HM: 'Heard et McDonald Islands',
		VA: 'Cité du Vatican',
		HN: 'Honduras',
		HK: 'Hong Kong',
		HU: 'Hongrie',
		IS: 'Islande',
		IN: 'Inde',
		ID: 'Indonésie',
		IR: 'Iran',
		IQ: 'Iraq',
		IE: 'Irlande',
		IM: 'Ile de Man',
		IL: 'Israël',
		IT: 'Italie',
		JM: 'Jamaïque',
		JP: 'Japon',
		JE: 'Jersey',
		JO: 'Jordan',
		KZ: 'Kazakhstan',
		KE: 'Kenya',
		KI: 'Kiribati',
		KP: 'Corée du Nord',
		KR: 'Corée du Sud',
		KW: 'Koweït',
		KG: 'Kirghizistan',
		LA: 'Laos',
		LV: 'Lettonie',
		LB: 'Liban',
		LS: 'Lesotho',
		LR: 'Libéria',
		LY: 'Libye',
		LI: 'Liechtenstein',
		LT: 'Lituanie',
		LU: 'Luxembourg',
		MO: 'Macao',
		MK: 'Macédoine',
		MG: 'Madagascar',
		MW: 'Malawi',
		MY: 'Malaisie',
		MV: 'Maldives',
		ML: 'Mali',
		MT: 'Malte',
		MH: 'Iles Marshall',
		MQ: 'Martinique',
		MR: 'Mauritanie',
		MU: 'Mauricio',
		YT: 'Mayotte',
		MX: 'Mexique',
		FM: 'Micronésie',
		MD: 'Moldavie',
		MC: 'Monaco',
		MN: 'Mongolie',
		MS: 'Montserrat',
		MA: 'Maroc',
		MZ: 'Mozambique',
		MM: 'Myanmar',
		NA: 'Namibie',
		NR: 'Nauru',
		NP: 'Népal',
		NL: 'Les Pays-Bas',
		AN: 'Antilles néerlandaises',
		NC: 'Nouvelle-Calédonie',
		NZ: 'Nouvelle-Zélande',
		NI: 'Nicaragua',
		NE: 'Niger',
		NG: 'Nigeria',
		NU: 'Niue',
		NF: 'Île de Norfolk',
		MP: 'Îles Mariannes du Nord',
		NO: 'Norvège',
		OM: 'Oman',
		PK: 'Pakistan',
		PW: 'Palau',
		PS: 'Palestine',
		PA: 'Panama',
		PG: 'Papouasie-Nouvelle-Guinée',
		PY: 'Paraguay',
		PE: 'Pérou',
		PH: 'Philippines',
		PN: 'Îles Pitcairn',
		PL: 'Pologne',
		PT: 'Portugal',
		PR: 'Porto Rico',
		QA: 'Qatar',
		RE: 'Réunion',
		RO: 'Roumanie',
		RU: 'Russie',
		RW: 'Rwanda',
		SH: 'Santa Helena',
		KN: 'Saint-Kitts-et-Nevis',
		LC: 'Santa Lucia',
		PM: 'Saint Pierre et Miquelon',
		VC: 'Saint Vincent et les Grenadines',
		WS: 'Samoa',
		SM: 'Saint-Marin',
		ST: 'Santo Tomé et Principe',
		SA: 'Arabie Saoudite',
		SN: 'Sénégal',
		CS: 'Serbie et Monténégro',
		SC: 'Seychelles',
		SL: 'Sierra Leone',
		SG: 'Singapour',
		SK: 'République slovaque',
		SI: 'Slovénie',
		SB: 'Îles Salomon',
		SO: 'Somalie',
		ZA: "République d'Afrique du Sud",
		GS: 'Géorgie du Sud et îles Sandwich du Sud',
		ES: 'Espagne',
		LK: 'Sri Lanka',
		SD: 'Soudan',
		SS: 'Soudan du Sud',
		SR: 'Suriname',
		SJ: 'Iles Svalbard et Jan Mayen',
		SZ: 'Swaziland',
		SE: 'Suède',
		CH: 'Suisse',
		SY: 'Syrie',
		TW: 'Taiwan',
		TJ: 'Tadjikistan',
		TZ: 'Tanzanie',
		TH: 'Thaïlande',
		TL: 'Timor oriental',
		TG: 'Togo',
		TK: 'Tokelau',
		TO: 'Tonga',
		TT: 'Trinité-et-Tobago',
		TN: 'Tunisie',
		TR: 'Turquía',
		TM: 'Turkménistan',
		TC: 'Îles Turques et Caïques',
		TV: 'Tuvalu',
		UG: 'Ouganda',
		UA: 'Ukraine',
		AE: 'Émirats arabes unis',
		GB: 'Royaume-Uni',
		US: "États-Unis d'Amérique",
		UM: 'Îles mineures éloignées des États-Unis',
		UY: 'Uruguay',
		UZ: 'Ouzbékistan',
		VU: 'Vanuatu',
		VE: 'Venezuela',
		VN: 'Vietnam',
		VG: 'Iles Vierges du Royaume-Uni',
		VI: 'Îles Vierges des États-Unis',
		WF: 'Wallis et Futuna',
		EH: 'Sahara occidental',
		YE: 'Yémen',
		ZM: 'Zambie',
		ZW: 'Zimbabwe',
	},
	EUS: {
		AF: 'Afganistan',
		AX: 'Åland uharteak',
		AL: 'Albania',
		DZ: 'Aljeria',
		AS: 'Samoa Estatu Batuetako',
		AD: 'Andorra',
		AO: 'Angola',
		AI: 'Anguila',
		AQ: 'Antartika',
		AG: 'Antigua eta Barbuda',
		AR: 'Argentina',
		AM: 'Armenia',
		AW: 'Aruba',
		AU: 'Australia',
		AT: 'Austria',
		AZ: 'Azerbaijan',
		BS: 'Bahamak',
		BH: 'Bahrein',
		BD: 'Bangladesh',
		BB: 'Barbados',
		BY: 'Bielorrusia',
		BE: 'Belgika',
		BZ: 'Belize',
		BJ: 'Benin',
		BM: 'Bermuda',
		BT: 'Bhutan',
		BO: 'Bolivia',
		BA: 'Bosnia eta Herzegovina',
		BW: 'Botswana',
		BV: 'Bouvet uhartea',
		BR: 'Brasil',
		IO: 'Indiako Ozeanoko Britainiar Lurraldea',
		BN: 'Bruneiko Darussalam',
		BG: 'Bulgaria',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Kanbodia',
		CM: 'Kamerun',
		CA: 'Kanada',
		CV: 'Ziurtagiri uhartea',
		KY: 'Kaiman uharteak',
		CF: 'Afrika Erdiko Errepublika',
		TD: 'Txad',
		CL: 'Txile',
		CN: 'Txina',
		CX: 'Christmas uhartea',
		CC: 'Cocos (Keeling) uharteak',
		CO: 'Kolonbia',
		KM: 'Comoreak',
		CG: 'Kongoko Errepublika',
		CD: 'Kongo (Err. Demokratikoa)',
		CK: 'Cook uharteak',
		CR: 'Costa Rica',
		CI: 'Boli Kosta',
		HR: 'Kroazia',
		CU: 'Kuba',
		CY: 'Zipre',
		CZ: 'Txekia Errepublika',
		DK: 'Danimarka',
		DJ: 'Djibuti',
		DM: 'Dominika',
		DO: 'Dominikar Errepublika',
		EC: 'Ekuador',
		EG: 'Egipto',
		SV: 'El Salvador',
		GQ: 'Ekuatore Ginea',
		ER: 'Eritrea',
		EE: 'Estonia',
		ET: 'Etiopia',
		FK: 'Malbina uharteak',
		FO: 'Faroe uharteak',
		FJ: 'Fiji',
		FI: 'Finlandia',
		FR: 'Frantzia',
		GF: 'Frantziako Guyana',
		PF: 'Polinesia Frantsesa',
		TF: 'Hegoaldeko eta Antartiko Frantsesak',
		GA: 'Gabon',
		GM: 'Gambia',
		GE: 'Georgia',
		DE: 'Alemania',
		GH: 'Ghana',
		GI: 'Gibraltar',
		GR: 'Grezia',
		GL: 'Groenlandia',
		GD: 'Granada',
		GP: 'Guadelupe',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernesey',
		GN: 'Ginea',
		GW: 'Ginea-Bissau',
		GY: 'Guyana',
		HT: 'Haiti',
		HM: 'Heard eta McDonald uharteak',
		VA: 'Vatikano Hiria',
		HN: 'Honduras',
		HK: 'Hong Kong',
		HU: 'Hungaria',
		IS: 'Islandia',
		IN: 'India',
		ID: 'Indonesia',
		IR: 'Iran',
		IQ: 'Irak',
		IE: 'Irlanda',
		IM: 'Man uhartea',
		IL: 'Israel',
		IT: 'Italia',
		JM: 'Jamaika',
		JP: 'Japonia',
		JE: 'Jersey',
		JO: 'Jordania',
		KZ: 'Kazakhstan',
		KE: 'Kenia',
		KI: 'Kiribati',
		KP: 'Korea Ipar',
		KR: 'Korea Hego',
		KW: 'Kuwait',
		KG: 'Kirgizistan',
		LA: 'Laos',
		LV: 'Letonia',
		LB: 'Libano',
		LS: 'Lesoto',
		LR: 'Liberia',
		LY: 'Libia',
		LI: 'Liechtenstein',
		LT: 'Lituania',
		LU: 'Luxenburgo',
		MO: 'Macau',
		MK: 'Mazedonia',
		MG: 'Madagaskar',
		MW: 'Malawi',
		MY: 'Malasia',
		MV: 'Maldivak',
		ML: 'Mali',
		MT: 'Malta',
		MH: 'Marshall uharteak',
		MQ: 'Martinika',
		MR: 'Mauritania',
		MU: 'Maurizio',
		YT: 'Mayotte',
		MX: 'Mexiko',
		FM: 'Mikronesia',
		MD: 'Moldavia',
		MC: 'Monako',
		MN: 'Mongolia',
		MS: 'Montserrat',
		MA: 'Maroko',
		MZ: 'Mozambike',
		MM: 'Myanmar',
		NA: 'Namibia',
		NR: 'Nauru',
		NP: 'Nepal',
		NL: 'Herbehereak',
		AN: 'Herbehereetako Antillak',
		NC: 'Kaledonia Berria',
		NZ: 'Zeelanda Berria',
		NI: 'Nikaragua',
		NE: 'Niger',
		NG: 'Nigeria',
		NU: 'Niue',
		NF: 'Norfolk uhartea',
		MP: 'Ipar Mariana uharteak',
		NO: 'Norvegia',
		OM: 'Oman',
		PK: 'Pakistan',
		PW: 'Palau',
		PS: 'Palestina',
		PA: 'Panama',
		PG: 'Papua Ginea Berria',
		PY: 'Paraguai',
		PE: 'Peru',
		PH: 'Filipinak',
		PN: 'Pitcairn uharteak',
		PL: 'Polonia',
		PT: 'Portugal',
		PR: 'Puerto Rico',
		QA: 'Qatar',
		RE: 'Reunion',
		RO: 'Errumania',
		RU: 'Errusia',
		RW: 'Ruanda',
		SH: 'Santa Helena',
		KN: 'San Cristobal eta Nieves',
		LC: 'Santa Luzia',
		PM: 'Saint Pierre eta Mikelune',
		VC: 'San Vicente eta Granadinak',
		WS: 'Samoa',
		SM: 'San Marino',
		ST: 'Sao Tome eta Principe',
		SA: 'Saudi Arabia',
		SN: 'Senegal',
		CS: 'Serbia eta Montenegro',
		SC: 'Seychelleak',
		SL: 'Sierra Leona',
		SG: 'Singapur',
		SK: 'Eslovakia',
		SI: 'Eslovenia',
		SB: 'Salomon uharteak',
		SO: 'Somalia',
		ZA: 'Hegoafrikako Errepublika',
		GS: 'Hegoaldeko Georgia eta Hego Sandwich uharteak',
		ES: 'Espainia',
		LK: 'Sri Lanka',
		SD: 'Sudan',
		SS: 'Sudan Hegoa',
		SR: 'Surinam',
		SJ: 'Svalbard eta Jan Mayen uharteak',
		SZ: 'Swazilandia',
		SE: 'Suedia',
		CH: 'Suitza',
		SY: 'Siria',
		TW: 'Taiwan',
		TJ: 'Tajikistan',
		TZ: 'Tanzania',
		TH: 'Tailandia',
		TL: 'Ekialdeko Timor',
		TG: 'Togo',
		TK: 'Tokelau',
		TO: 'Tonga',
		TT: 'Trinidad eta Tobago',
		TN: 'Tunisia',
		TR: 'Turkia',
		TM: 'Turkmenistan',
		TC: 'Turk eta Caicos uharteak',
		TV: 'Tuvalu',
		UG: 'Uganda',
		UA: 'Ukraina',
		AE: 'Arabiar Emirrerri Batuak',
		GB: 'Erresuma Batua',
		US: 'Ameriketako Estatu Batuak',
		UM: 'Ameriketako Estatu Batuetako Uharte Txikiak',
		UY: 'Uruguai',
		UZ: 'Uzbekistan',
		VU: 'Vanuatu',
		VE: 'Venezuela',
		VN: 'Vietnam',
		VG: 'Britainiar Virgin uharteak',
		VI: 'Estatu Batuetako Virgin uharteak',
		WF: 'Wallis eta Futuna',
		EH: 'Mendebaldeko Sahara',
		YE: 'Yemen',
		ZM: 'Zambia',
		ZW: 'Zimbabwe',
	},
	ITA: {
		AF: 'Afghanistan',
		AX: 'Isole Åland',
		AL: 'Albania',
		DZ: 'Algeria',
		AS: 'Samoa Americane',
		AD: 'Andorra',
		AO: 'Angola',
		AI: 'Anguilla',
		AQ: 'Antartide',
		AG: 'Antigua e Barbuda',
		AR: 'Argentina',
		AM: 'Armenia',
		AW: 'Aruba',
		AU: 'Australia',
		AT: 'Austria',
		AZ: 'Azerbaigian',
		BS: 'Bahamas',
		BH: 'Bahrein',
		BD: 'Bangladesh',
		BB: 'Barbados',
		BY: 'Bielorussia',
		BE: 'Belgio',
		BZ: 'Belize',
		BJ: 'Benin',
		BM: 'Bermuda',
		BT: 'Bhutan',
		BO: 'Bolivia',
		BA: 'Bosnia-Erzegovina',
		BW: 'Botswana',
		BV: 'Isola Bouvet',
		BR: 'Brasile',
		IO: "Territorio Britannico dell'Oceano Indiano",
		BN: 'Brunei Darussalam',
		BG: 'Bulgaria',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Cambogia',
		CM: 'Camerun',
		CA: 'Canada',
		CV: 'Capo Verde',
		KY: 'Isole Cayman',
		CF: 'Repubblica Centrafricana',
		TD: 'Ciad',
		CL: 'Cile',
		CN: 'Cina',
		CX: 'Isola di Natale',
		CC: 'Isole Cocos (Keeling)',
		CO: 'Colombia',
		KM: 'Comore',
		CG: 'Congo',
		CD: 'Congo (Repubblica Democratica del)',
		CK: 'Isole Cook',
		CR: 'Costa Rica',
		CI: "Costa d'Avorio",
		HR: 'Croazia',
		CU: 'Cuba',
		CY: 'Cipro',
		CZ: 'Repubblica Ceca',
		DK: 'Danimarca',
		DJ: 'Gibuti',
		DM: 'Dominica',
		DO: 'Repubblica Dominicana',
		EC: 'Ecuador',
		EG: 'Egitto',
		SV: 'El Salvador',
		GQ: 'Guinea Equatoriale',
		ER: 'Eritrea',
		EE: 'Estonia',
		ET: 'Etiopia',
		FK: 'Isole Falkland',
		FO: 'Isole Faroe',
		FJ: 'Figi',
		FI: 'Finlandia',
		FR: 'Francia',
		GF: 'Guyana Francese',
		PF: 'Polinesia Francese',
		TF: 'Terre australi e antartiche francesi',
		GA: 'Gabon',
		GM: 'Gambia',
		GE: 'Georgia',
		DE: 'Germania',
		GH: 'Ghana',
		GI: 'Gibilterra',
		GR: 'Grecia',
		GL: 'Groenlandia',
		GD: 'Grenada',
		GP: 'Guadalupa',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernsey',
		GN: 'Guinea',
		GW: 'Guinea-Bissau',
		GY: 'Guyana',
		HT: 'Haiti',
		HM: 'Isole Heard e McDonald',
		VA: 'Città del Vaticano',
		HN: 'Honduras',
		HK: 'Hong Kong',
		HU: 'Ungheria',
		IS: 'Islanda',
		IN: 'India',
		ID: 'Indonesia',
		IR: 'Iran',
		IQ: 'Iraq',
		IE: 'Irlanda',
		IM: 'Isola di Man',
		IL: 'Israele',
		IT: 'Italia',
		JM: 'Giamaica',
		JP: 'Giappone',
		JE: 'Jersey',
		JO: 'Giordania',
		KZ: 'Kazakistan',
		KE: 'Kenya',
		KI: 'Kiribati',
		KP: 'Corea del Nord',
		KR: 'Corea del Sud',
		KW: 'Kuwait',
		KG: 'Kirghizistan',
		LA: 'Laos',
		LV: 'Lettonia',
		LB: 'Libano',
		LS: 'Lesotho',
		LR: 'Liberia',
		LY: 'Libia',
		LI: 'Liechtenstein',
		LT: 'Lituania',
		LU: 'Lussemburgo',
		MO: 'Macao',
		MK: 'Macedonia',
		MG: 'Madagascar',
		MW: 'Malawi',
		MY: 'Malesia',
		MV: 'Maldive',
		ML: 'Mali',
		MT: 'Malta',
		MH: 'Isole Marshall',
		MQ: 'Martinica',
		MR: 'Mauritania',
		MU: 'Maurizio',
		YT: 'Mayotte',
		MX: 'Messico',
		FM: 'Micronesia',
		MD: 'Moldavia',
		MC: 'Monaco',
		MN: 'Mongolia',
		MS: 'Montserrat',
		MA: 'Marocco',
		MZ: 'Mozambico',
		MM: 'Myanmar',
		NA: 'Namibia',
		NR: 'Nauru',
		NP: 'Nepal',
		NL: 'Paesi Bassi',
		AN: 'Antille Olandesi',
		NC: 'Nuova Caledonia',
		NZ: 'Nuova Zelanda',
		NI: 'Nicaragua',
		NE: 'Niger',
		NG: 'Nigeria',
		NU: 'Niue',
		NF: 'Isola Norfolk',
		MP: 'Isole Marianne Settentrionali',
		NO: 'Norvegia',
		OM: 'Oman',
		PK: 'Pakistan',
		PW: 'Palau',
		PS: 'Palestina',
		PA: 'Panama',
		PG: 'Papua Nuova Guinea',
		PY: 'Paraguay',
		PE: 'Perù',
		PH: 'Filippine',
		PN: 'Isole Pitcairn',
		PL: 'Polonia',
		PT: 'Portogallo',
		PR: 'Porto Rico',
		QA: 'Qatar',
		RE: 'Riunione',
		RO: 'Romania',
		RU: 'Russia',
		RW: 'Ruanda',
		SH: "Sant'Elena",
		KN: 'Saint Kitts e Nevis',
		LC: 'Santa Lucia',
		PM: 'Saint-Pierre e Miquelon',
		VC: 'Saint Vincent e Grenadine',
		WS: 'Samoa',
		SM: 'San Marino',
		ST: 'São Tomé e Príncipe',
		SA: 'Arabia Saudita',
		SN: 'Senegal',
		CS: 'Serbia e Montenegro',
		SC: 'Seychelles',
		SL: 'Sierra Leone',
		SG: 'Singapore',
		SK: 'Slovacchia',
		SI: 'Slovenia',
		SB: 'Isole Salomone',
		SO: 'Somalia',
		ZA: 'Sudafrica',
		GS: 'Georgia del Sud e Isole Sandwich del Sud',
		ES: 'Spagna',
		LK: 'Sri Lanka',
		SD: 'Sudan',
		SS: 'Sudan del Sud',
		SR: 'Suriname',
		SJ: 'Svalbard e Jan Mayen',
		SZ: 'Swazilandia',
		SE: 'Svezia',
		CH: 'Svizzera',
		SY: 'Siria',
		TW: 'Taiwan',
		TJ: 'Tagikistan',
		TZ: 'Tanzania',
		TH: 'Thailandia',
		TL: 'Timor Est',
		TG: 'Togo',
		TK: 'Tokelau',
		TO: 'Tonga',
		TT: 'Trinidad e Tobago',
		TN: 'Tunisia',
		TR: 'Turchia',
		TM: 'Turkmenistan',
		TC: 'Isole Turks e Caicos',
		TV: 'Tuvalu',
		UG: 'Uganda',
		UA: 'Ucraina',
		AE: 'Emirati Arabi Uniti',
		GB: 'Regno Unito',
		US: "Stati Uniti d'America",
		UM: 'Isole minori esterne degli Stati Uniti',
		UY: 'Uruguay',
		UZ: 'Uzbekistan',
		VU: 'Vanuatu',
		VE: 'Venezuela',
		VN: 'Vietnam',
		VG: 'Isole Vergini britanniche',
		VI: 'Isole Vergini americane',
		WF: 'Wallis e Futuna',
		EH: 'Sahara Occidentale',
		YE: 'Yemen',
		ZM: 'Zambia',
		ZW: 'Zimbabwe',
	},
	GLG: {
		AF: 'Afganistán',
		AX: 'Alandia',
		AL: 'Albania',
		DZ: 'Arxelia',
		AS: 'Samoa',
		AD: 'Andorra',
		AO: 'Angola',
		AI: 'Anguila',
		AQ: 'Antártida',
		AG: 'Antiga e Barbuda',
		AR: 'Arxentina',
		AM: 'Armenia',
		AW: 'Aruba',
		AU: 'Australia',
		AT: 'Austria',
		AZ: 'Acerbaixán',
		BS: 'Bahamas',
		BH: 'Baréin',
		BD: 'Bangladés',
		BB: 'Barbados',
		BY: 'Belarús',
		BE: 'Bélxica',
		BZ: 'Belice',
		BJ: 'Benín',
		BM: 'Bermudas',
		BT: 'Bután',
		BO: 'Bolivia',
		BA: 'Bosnia e Hercegovina',
		BW: 'Botsuana',
		BV: 'Illa Bouvet',
		BR: 'Brasil',
		IO: 'Territorio Británico do Océano Índico',
		BN: 'Brunéi',
		BG: 'Bulgaria',
		BF: 'Burkina Faso',
		BI: 'Burundi',
		KH: 'Camboxa',
		CM: 'Camerún',
		CA: 'Canadá',
		CV: 'Cabo Verde',
		KY: 'Illas Caimán',
		CF: 'República Centroafricana',
		TD: 'Chad',
		CL: 'Chile',
		CN: 'China',
		CX: 'Illa de Nadal',
		CC: 'Illas Cocos (Keeling)',
		CO: 'Colombia',
		KM: 'Comores',
		CG: 'Congo',
		CD: 'Congo (Rep. Dem.)',
		CK: 'Illas Cook',
		CR: 'Costa Rica',
		CI: 'Costa de Marfil',
		HR: 'Croacia',
		CU: 'Cuba',
		CY: 'Chipre',
		CZ: 'República Checa',
		DK: 'Dinamarca',
		DJ: 'Xibuti',
		DM: 'Dominica',
		DO: 'República Dominicana',
		EC: 'Exuador',
		EG: 'Exipto',
		SV: 'El Salvador',
		GQ: 'Guinea Ecuatorial',
		ER: 'Eritrea',
		EE: 'Extonia',
		ET: 'Etiopía',
		FK: 'Illas Malvinas',
		FO: 'Illas Feroe',
		FJ: 'Fixi',
		FI: 'Finlandia',
		FR: 'Francia',
		GF: 'Güiana Francesa',
		PF: 'Polinesia Francesa',
		TF: 'Terras Austrais e Antárticas Francesas',
		GA: 'Gabón',
		GM: 'Gambia',
		GE: 'Xeorxia',
		DE: 'Alemaña',
		GH: 'Ghana',
		GI: 'Xibraltar',
		GR: 'Grecia',
		GL: 'Groenlandia',
		GD: 'Granada',
		GP: 'Guadalupe',
		GU: 'Guam',
		GT: 'Guatemala',
		GG: 'Guernsey',
		GN: 'Guinea',
		GW: 'Guinea-Bisáu',
		GY: 'Güiana',
		HT: 'Haití',
		HM: 'Illas Heard e McDonald',
		VA: 'Cidade do Vaticano',
		HN: 'Honduras',
		HK: 'Hong Kong',
		HU: 'Hungría',
		IS: 'Islandia',
		IN: 'India',
		ID: 'Indonesia',
		IR: 'Irán',
		IQ: 'Iraq',
		IE: 'Irlanda',
		IM: 'Illa de Man',
		IL: 'Israel',
		IT: 'Italia',
		JM: 'Xamaica',
		JP: 'Xapón',
		JE: 'Jersey',
		JO: 'Xordania',
		KZ: 'Kazakistan',
		KE: 'Quenia',
		KI: 'Kiribati',
		KP: 'Corea do Norte',
		KR: 'Corea do Sur',
		KW: 'Kuwait',
		KG: 'Quirguicistán',
		LA: 'Laos',
		LV: 'Letonia',
		LB: 'Líbano',
		LS: 'Lesoto',
		LR: 'Liberia',
		LY: 'Libia',
		LI: 'Liechtenstein',
		LT: 'Lituania',
		LU: 'Luxemburgo',
		MO: 'Macau',
		MK: 'Macedonia',
		MG: 'Madagascar',
		MW: 'Malaui',
		MY: 'Malaisia',
		MV: 'Maldivas',
		ML: 'Malí',
		MT: 'Malta',
		MH: 'Illas Marshall',
		MQ: 'Martinica',
		MR: 'Mauritania',
		MU: 'Mauricio',
		YT: 'Mayotte',
		MX: 'México',
		FM: 'Micronesia',
		MD: 'Moldavia',
		MC: 'Mónaco',
		MN: 'Mongolia',
		MS: 'Montserrat',
		MA: 'Marrocos',
		MZ: 'Mozambique',
		MM: 'Myanmar',
		NA: 'Namibia',
		NR: 'Nauru',
		NP: 'Nepal',
		NL: 'Países Baixos',
		AN: 'Antillas Neerlandesas',
		NC: 'Nueva Caledonia',
		NZ: 'Nova Celandia',
		NI: 'Nicaragua',
		NE: 'Níxer',
		NG: 'Nixeria',
		NU: 'Niue',
		NF: 'Illa Norfolk',
		MP: 'Illas Marianas do Norte',
		NO: 'Noruega',
		OM: 'Omán',
		PK: 'Paquistán',
		PW: 'Palau',
		PS: 'Palestina',
		PA: 'Panamá',
		PG: 'Papúa Nova Guinea',
		PY: 'Paraguai',
		PE: 'Perú',
		PH: 'Filipinas',
		PN: 'Illas Pitcairn',
		PL: 'Polonia',
		PT: 'Portugal',
		PR: 'Porto Rico',
		QA: 'Qatar',
		RE: 'Reunión',
		RO: 'Romanía',
		RU: 'Rusia',
		RW: 'Ruanda',
		SH: 'Santa Helena',
		KN: 'San Cristovo e Nevis',
		LC: 'Santa Lucía',
		PM: 'San Pedro e Miquelón',
		VC: 'San Vicente e Granadinas',
		WS: 'Samoa',
		SM: 'San Marino',
		ST: 'Santo Tomé e Príncipe',
		SA: 'Arabia Saudita',
		SN: 'Senegal',
		CS: 'Serbia e Montenegro',
		SC: 'Seixeles',
		SL: 'Serra Leoa',
		SG: 'Singapur',
		SK: 'Eslovaquia',
		SI: 'Eslovenia',
		SB: 'Illas Salomón',
		SO: 'Somalia',
		ZA: 'Sudáfrica',
		GS: 'Illas Xeorxia do Sur e Sandwich do Sur',
		ES: 'España',
		LK: 'Sri Lanka',
		SD: 'Sudán',
		SS: 'Sudán do Sur',
		SR: 'Surinam',
		SJ: 'Illas Svalbard e Jan Mayen',
		SZ: 'Suacilandia',
		SE: 'Suecia',
		CH: 'Suíza',
		SY: 'Siria',
		TW: 'Taiwán',
		TJ: 'Taxiquistán',
		TZ: 'Tanzania',
		TH: 'Tailandia',
		TL: 'Timor-Leste',
		TG: 'Togo',
		TK: 'Toquelau',
		TO: 'Tonga',
		TT: 'Trinidade e Tobago',
		TN: 'Tunisia',
		TR: 'Turquía',
		TM: 'Turcomenistán',
		TC: 'Illas Turks e Caicos',
		TV: 'Tuvalu',
		UG: 'Uganda',
		UA: 'Ucraína',
		AE: 'Emiratos Árabes Unidos',
		GB: 'Reino Unido',
		US: 'Estados Unidos de América',
		UM: 'Illas Ultramarinas Menores dos Estados Unidos',
		UY: 'Uruguai',
		UZ: 'Uzbekistán',
		VU: 'Vanuatu',
		VE: 'Venezuela',
		VN: 'Vietnam',
		VG: 'Illas Virxes Británicas',
		VI: 'Illas Virxes Estadounidenses',
		WF: 'Wallis e Futuna',
		EH: 'Sáhara Occidental',
		YE: 'Iemen',
		ZM: 'Zambia',
		ZW: 'Zimbabue',
	},
};

export const getTranslatedCountry = (countryCode: string, locale: Locale | undefined): string | undefined => {
	if (!locale) return;
	return countryTranslations[localeToLanguage[locale]]?.[countryCode];
};

export class CoreDomainError extends Error {}

export type DocumentType = 'id' | 'taxId' | 'passport' | 'nie' | 'others';

export const currencyCodes: Record<string, { exponent: string; name: string }> = {
	AED: {
		name: 'UAE Dirham',
		exponent: '2',
	},
	ALL: {
		name: 'Albanian Lek',
		exponent: '2',
	},
	AMD: {
		name: 'Armenian Dram',
		exponent: '2',
	},
	ANG: {
		name: 'Antillian Guilder',
		exponent: '2',
	},
	ARS: {
		name: 'Argentine Peso',
		exponent: '2',
	},
	AUD: {
		name: 'Australian Dollar',
		exponent: '2',
	},
	AWG: {
		name: 'Aruban Guilder',
		exponent: '2',
	},
	AZN: {
		name: 'Azerbaijani manat',
		exponent: '2',
	},
	BAM: {
		name: 'Bosnia and Herzegovina Convertible Marks',
		exponent: '2',
	},
	BBD: {
		name: 'Barbados Dollar',
		exponent: '2',
	},
	BDT: {
		name: 'Bangladesh Taka',
		exponent: '2',
	},
	BGL: {
		name: 'Bulgaria Lev',
		exponent: '2',
	},
	BGN: {
		name: 'New Bulgarian Lev',
		exponent: '2',
	},
	BHD: {
		name: 'Bahraini Dinar',
		exponent: '3',
	},
	BMD: {
		name: 'Bermudian Dollar',
		exponent: '2',
	},
	BND: {
		name: 'Brunei Dollar',
		exponent: '2',
	},
	BOB: {
		name: 'Bolivia Boliviano',
		exponent: '2',
	},
	BRL: {
		name: 'Brazilian Real',
		exponent: '2',
	},
	BSD: {
		name: 'Bahamian Dollar',
		exponent: '2',
	},
	BWP: {
		name: 'Botswana Pula',
		exponent: '2',
	},
	BYN: {
		name: 'New Belarusian Ruble',
		exponent: '2',
	},
	BZD: {
		name: 'Belize Dollar',
		exponent: '2',
	},
	CAD: {
		name: 'Canadian Dollar',
		exponent: '2',
	},
	CHF: {
		name: 'Swiss Franc',
		exponent: '2',
	},
	CLP: {
		name: 'Chilean Peso',
		exponent: '2',
	},
	CNY: {
		name: 'Yuan Renminbi',
		exponent: '2',
	},
	COP: {
		name: 'Colombian Peso',
		exponent: '2',
	},
	CRC: {
		name: 'Costa Rican Colon',
		exponent: '2',
	},
	CSD: {
		name: 'Serbian Dinar',
		exponent: '2',
	},
	CVE: {
		name: 'Cape Verdi Escudo',
		exponent: '0',
	},
	CZK: {
		name: 'Czech Koruna',
		exponent: '2',
	},
	DJF: {
		name: 'Djibouti Franc',
		exponent: '0',
	},
	DKK: {
		name: 'Danish Krone',
		exponent: '2',
	},
	DOP: {
		name: 'Dominican Republic Peso',
		exponent: '2',
	},
	DZD: {
		name: 'Algerian Dinar',
		exponent: '2',
	},
	LAK: {
		name: 'Laos Kip',
		exponent: '2',
	},
	LBP: {
		name: 'Lebanese Pound',
		exponent: '2',
	},
	LKR: {
		name: 'Sri Lanka Rupee',
		exponent: '2',
	},
	LTL: {
		name: 'Lithuanian Litas',
		exponent: '2',
	},
	LVL: {
		name: 'Latvian Lats',
		exponent: '2',
	},
	LYD: {
		name: 'Libyan Dinar',
		exponent: '3',
	},
	MAD: {
		name: 'Moroccan Dirham',
		exponent: '2',
	},
	MDL: {
		name: 'Moldovia Leu',
		exponent: '2',
	},
	MNT: {
		name: 'Mongolia Tugrik',
		exponent: '2',
	},
	MOP: {
		name: 'Macau Pataca',
		exponent: '2',
	},
	MRO: {
		name: 'Mauritania Ouguiya',
		exponent: '1',
	},
	MUR: {
		name: 'Mauritius Rupee',
		exponent: '2',
	},
	MVR: {
		name: 'Maldives Rufiyaa',
		exponent: '2',
	},
	MWK: {
		name: 'Malawi Kwacha',
		exponent: '2',
	},
	MXN: {
		name: 'Mexican Peso',
		exponent: '2',
	},
	MXP: {
		name: 'Mexican Peso',
		exponent: '2',
	},
	MYR: {
		name: 'Malaysian Ringgit',
		exponent: '2',
	},
	NAD: {
		name: 'Namibian Dollar',
		exponent: '2',
	},
	NGN: {
		name: 'Nigerian Naira',
		exponent: '2',
	},
	NIO: {
		name: 'Nicaragua Cordoba Oro',
		exponent: '2',
	},
	NOK: {
		name: 'Norwegian Krone',
		exponent: '2',
	},
	NPR: {
		name: 'Nepalese Rupee',
		exponent: '2',
	},
	NZD: {
		name: 'New Zealand Dollar',
		exponent: '2',
	},
	OMR: {
		name: 'Rial Omani',
		exponent: '3',
	},
	PAB: {
		name: 'Panamanian Balboa',
		exponent: '2',
	},
	PEN: {
		name: 'Peruvian Nuevo Sol',
		exponent: '2',
	},
	PGK: {
		name: 'New Guinea Kina',
		exponent: '2',
	},
	PHP: {
		name: 'Philippine Peso',
		exponent: '2',
	},
	PKR: {
		name: 'Pakistan Rupee',
		exponent: '2',
	},
	PLN: {
		name: 'New Polish Zloty',
		exponent: '2',
	},
	PYG: {
		name: 'Paraguay Guarani',
		exponent: '0',
	},
	QAR: {
		name: 'Qatari Rial',
		exponent: '2',
	},
	ROL: {
		name: 'Romanian Lei',
		exponent: '2',
	},
	RON: {
		name: 'New Romanian Lei',
		exponent: '2',
	},
	RSD: {
		name: 'Serbian Dinar',
		exponent: '2',
	},
	RUB: {
		name: 'Russian Ruble',
		exponent: '2',
	},
	EEK: {
		name: 'Estonian Krone',
		exponent: '2',
	},
	EGP: {
		name: 'Egyptian Pound',
		exponent: '2',
	},
	ETB: {
		name: 'Ethiopian Birr',
		exponent: '2',
	},
	EUR: {
		name: 'Euro',
		exponent: '2',
	},
	FJD: {
		name: 'Fiji Dollar',
		exponent: '2',
	},
	FKP: {
		name: 'Falkland Islands Pound',
		exponent: '2',
	},
	GBP: {
		name: 'Pound Sterling',
		exponent: '2',
	},
	GEL: {
		name: 'Georgian Lari',
		exponent: '2',
	},
	GHC: {
		name: 'Ghanaian Cedi (2nd)',
		exponent: '0',
	},
	GHS: {
		name: 'Ghanaian Cedi (3rd)',
		exponent: '2',
	},
	GIP: {
		name: 'Gibraltar Pound',
		exponent: '2',
	},
	GMD: {
		name: 'Gambia Delasi',
		exponent: '2',
	},
	GNF: {
		name: 'Guinea Franc',
		exponent: '0',
	},
	GTQ: {
		name: 'Guatemala Quetzal',
		exponent: '2',
	},
	GYD: {
		name: 'Guyanese Dollar',
		exponent: '2',
	},
	HKD: {
		name: 'Hong Kong Dollar',
		exponent: '2',
	},
	HNL: {
		name: 'Honduras Lempira',
		exponent: '2',
	},
	HRK: {
		name: 'Croatia Kuna',
		exponent: '2',
	},
	HTG: {
		name: 'Haitian Gourde',
		exponent: '2',
	},
	HUF: {
		name: 'Hungarian Forint',
		exponent: '2',
	},
	IDR: {
		name: 'Indonesian Rupiah',
		exponent: '0',
	},
	ILS: {
		name: 'New Israeli Scheqel',
		exponent: '2',
	},
	INR: {
		name: 'Indian Rupee',
		exponent: '2',
	},
	ISK: {
		name: 'Iceland Krona',
		exponent: '2',
	},
	JMD: {
		name: 'Jamaican Dollar',
		exponent: '2',
	},
	JOD: {
		name: 'Jordanian Dinar',
		exponent: '3',
	},
	JPY: {
		name: 'Japanese Yen',
		exponent: '0',
	},
	KES: {
		name: 'Kenyan Shilling',
		exponent: '2',
	},
	KGS: {
		name: 'Kyrgyzstan Som',
		exponent: '2',
	},
	KHR: {
		name: 'Cambodia Riel',
		exponent: '2',
	},
	KMF: {
		name: 'Comoro Franc',
		exponent: '0',
	},
	KRW: {
		name: 'South-Korean Won',
		exponent: '0',
	},
	KWD: {
		name: 'Kuwaiti Dinar',
		exponent: '3',
	},
	KYD: {
		name: 'Cayman Islands Dollar',
		exponent: '2',
	},
	KZT: {
		name: 'Kazakhstani Tenge',
		exponent: '2',
	},
	RWF: {
		name: 'Rwanda Franc',
		exponent: '0',
	},
	SAR: {
		name: 'Saudi Riyal',
		exponent: '2',
	},
	SBD: {
		name: 'Solomon Island Dollar',
		exponent: '2',
	},
	SCR: {
		name: 'Seychelles Rupee',
		exponent: '2',
	},
	SEK: {
		name: 'Swedish Krone',
		exponent: '2',
	},
	SGD: {
		name: 'Singapore Dollar',
		exponent: '2',
	},
	SHP: {
		name: 'St. Helena Pound',
		exponent: '2',
	},
	SKK: {
		name: 'Slovak Koruna',
		exponent: '2',
	},
	SLL: {
		name: 'Sierra Leone',
		exponent: '2',
	},
	SOS: {
		name: 'Somalia Shilling',
		exponent: '2',
	},
	STD: {
		name: 'Sao Tome & Principe Dobra',
		exponent: '2',
	},
	SVC: {
		name: 'El Salvador Colón',
		exponent: '2',
	},
	SZL: {
		name: 'Swaziland Lilangeni',
		exponent: '2',
	},
	THB: {
		name: 'Thai Baht',
		exponent: '2',
	},
	TND: {
		name: 'Tunisian Dinar',
		exponent: '3',
	},
	TOP: {
		name: "Tonga Pa'anga",
		exponent: '2',
	},
	TRY: {
		name: 'New Turkish Lira',
		exponent: '2',
	},
	TTD: {
		name: 'Trinidad & Tobago Dollar',
		exponent: '2',
	},
	TWD: {
		name: 'New Taiwan Dollar',
		exponent: '2',
	},
	TZS: {
		name: 'Tanzanian Shilling',
		exponent: '2',
	},
	UAH: {
		name: 'Ukraine Hryvnia',
		exponent: '2',
	},
	UGX: {
		name: 'Uganda Shilling',
		exponent: '0',
	},
	USD: {
		name: 'US Dollars',
		exponent: '2',
	},
	UYU: {
		name: 'Peso Uruguayo',
		exponent: '2',
	},
	UZS: {
		name: 'Uzbekistani Som',
		exponent: '2',
	},
	VEF: {
		name: 'Venezuelan Bolívar',
		exponent: '2',
	},
	VND: {
		name: 'Vietnamese New Dong',
		exponent: '0',
	},
	VUV: {
		name: 'Vanuatu Vatu',
		exponent: '0',
	},
	WST: {
		name: 'Samoan Tala',
		exponent: '2',
	},
	XAF: {
		name: 'CFA Franc BEAC',
		exponent: '0',
	},
	XCD: {
		name: 'East Carribean Dollar',
		exponent: '2',
	},
	XOF: {
		name: 'CFA Franc BCEAO',
		exponent: '0',
	},
	XPF: {
		name: 'CFP Franc',
		exponent: '0',
	},
	YER: {
		name: 'Yemeni Rial',
		exponent: '2',
	},
	ZAR: {
		name: 'South African Rand',
		exponent: '2',
	},
	ZMW: {
		name: 'Zambia Kwacha',
		exponent: '2',
	},
};

export function amountToMinorUnits(value: number, currency: string): number {
	return value * Math.pow(10, Number(currencyCodes[currency]!.exponent));
}
